import { proxy, subscribe } from 'valtio';
import { useSnapshot } from 'valtio/react';

import type { StoreHit } from '@/lib/algolia';
import type { WatchProductSummary } from '@/lib/service/ecs/contracts';
import type {
  RepairDeliveryMethod,
  RepairReturnMethod,
  RepairShippingKitOption,
} from '@/lib/service/ecs/logisticsCapabilities';
import type { Watch } from '@/lib/myWatches/types';

import { clearRepairRequestSubmitState } from './submitState';
import { clearRepairRequestState, loadRepairRequestState, saveRepairRequestState } from './storage';
import {
  REPAIR_REQUEST_INITIAL_STATE,
  type RepairRequestReason,
  type RepairRequestState,
  type RepairRequestStoredAddress,
} from './types';

export const repairRequestState = proxy<RepairRequestState>({ ...REPAIR_REQUEST_INITIAL_STATE });

let hydrated = false;

export function hydrateRepairRequestState(): void {
  if (hydrated || globalThis.window === undefined) return;

  const stored = loadRepairRequestState();
  Object.assign(repairRequestState, stored);
  hydrated = true;
}

subscribe(repairRequestState, () => {
  saveRepairRequestState({
    reasons: repairRequestState.reasons,
    reasonComment: repairRequestState.reasonComment,
    serialNumber: repairRequestState.serialNumber,
    referenceNumber: repairRequestState.referenceNumber,
    watchWarranty: repairRequestState.watchWarranty,
    watchProductSummary: repairRequestState.watchProductSummary,
    deliveryMethod: repairRequestState.deliveryMethod,
    returnMethod: repairRequestState.returnMethod,
    shippingKitOption: repairRequestState.shippingKitOption,
    serviceBoutique: repairRequestState.serviceBoutique,
    shippingAddress: repairRequestState.shippingAddress,
    billingAddress: repairRequestState.billingAddress,
    differentBillingAddress: repairRequestState.differentBillingAddress,
    email: repairRequestState.email,
    phone: repairRequestState.phone,
    phonePrefixCode: repairRequestState.phonePrefixCode,
  });
});

export function useRepairRequestState() {
  return useSnapshot(repairRequestState);
}

export interface RepairRequestPersonalDetailsPayload {
  deliveryMethod: RepairDeliveryMethod;
  returnMethod: RepairReturnMethod;
  shippingKitOption: RepairShippingKitOption | null;
  serviceBoutique: StoreHit | null;
  shippingAddress: RepairRequestStoredAddress;
  billingAddress: RepairRequestStoredAddress;
  differentBillingAddress: boolean;
  email: string;
  phone: string;
  phonePrefixCode: string;
}

export const repairRequestActions = {
  setReasons(reasons: RepairRequestReason[]) {
    repairRequestState.reasons = reasons;
  },
  setReasonComment(comment: string) {
    repairRequestState.reasonComment = comment;
  },
  setWatchContext(context: {
    serialNumber: string | null;
    referenceNumber: string | null;
    watchWarranty: Watch | null;
    watchProductSummary: WatchProductSummary | null;
  }) {
    repairRequestState.serialNumber = context.serialNumber;
    repairRequestState.referenceNumber = context.referenceNumber;
    repairRequestState.watchWarranty = context.watchWarranty;
    repairRequestState.watchProductSummary = context.watchProductSummary;
  },
  setPersonalDetails(payload: RepairRequestPersonalDetailsPayload) {
    repairRequestState.deliveryMethod = payload.deliveryMethod;
    repairRequestState.returnMethod = payload.returnMethod;
    repairRequestState.shippingKitOption = payload.shippingKitOption;
    repairRequestState.serviceBoutique = payload.serviceBoutique;
    repairRequestState.shippingAddress = payload.shippingAddress;
    repairRequestState.billingAddress = payload.billingAddress;
    repairRequestState.differentBillingAddress = payload.differentBillingAddress;
    repairRequestState.email = payload.email;
    repairRequestState.phone = payload.phone;
    repairRequestState.phonePrefixCode = payload.phonePrefixCode;
  },
  clearWatchContext() {
    repairRequestState.serialNumber = null;
    repairRequestState.referenceNumber = null;
    repairRequestState.watchWarranty = null;
    repairRequestState.watchProductSummary = null;
  },
  clear() {
    Object.assign(repairRequestState, REPAIR_REQUEST_INITIAL_STATE);
    clearRepairRequestState();
    clearRepairRequestSubmitState();
  },
};
